export enum NotificationPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum NotificationStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  ARCHIVED = 'ARCHIVED',
}

export enum NotificationChannel {
  EMAIL = 'email',
  WEB = 'web',
  // Backward compatibility with clickdummy
  SMS = 'SMS',
}

export enum NotificationMessageType {
  SYSTEM = 'S',
  MESSAGE = 'M',
  APPOINTMENT = 'A',
}

export enum NotificationQuickaction {
  NONE = 'N',
  APPOINTMENT = 'A',
  INTERNAL_LINK = 'I',
  EXTERNAL_LINK = 'E',
}

export enum EmailNotificationInterval {
  IMMEDIATE = 'I',
  DAILY = 'D',
  WEEKLY = 'W',
}

export enum EmailNotificationDay {
  MONDAY = 0,
  TUESDAY = 1,
  WEDNESDAY = 2,
  THURSDAY = 3,
  FRIDAY = 4,
  SATURDAY = 5,
  SUNDAY = 6,
}

export interface UserProfile {
  email_notification_interval?: EmailNotificationInterval;
  email_notification_day?: EmailNotificationDay;
  email_notification_time?: string;
}

export interface UserProfileRequest {
  email_notification_interval?: EmailNotificationInterval;
  email_notification_day?: EmailNotificationDay;
  email_notification_time?: string;
}

export interface PatchedUserProfileRequest {
  email_notification_interval?: EmailNotificationInterval;
  email_notification_day?: EmailNotificationDay;
  email_notification_time?: string;
}

export interface NotificationAction {
  label: string;
  actionUrl?: string;
  internalRoute?: string;
  icon?: string;
}

/**
 * Raw DTO from OpenAPI Backend
 */
export interface NotificationDTO {
  id: string;
  title: string;
  message?: string | null;
  created_at: string;
  read_at?: string | null;
  reminder_at?: string | null;
  due_date?: string | null;
  status: NotificationStatus;
  priority?: NotificationPriority | null;
  icon?: string | null;
  internal_link?: string | null;
  external_link?: string | null;
  channel?: NotificationChannel | string | null;
  actions?: NotificationAction[] | Record<string, any> | null;
  metadata?: Record<string, any> | null;
  message_type?: NotificationMessageType | string | null;
  follow_up?: string | null;
  quickaction?: NotificationQuickaction | string | null;
  quickaction_value?: string | null;
}

/**
 * Full update request model according to OpenAPI NotificationRequest
 */
export interface NotificationRequest {
  title: string;
  message?: string | null;
  read_at?: string | null;
  reminder_at?: string | null;
  due_date?: string | null;
  status?: NotificationStatus;
  priority?: NotificationPriority;
  icon?: string | null;
  internal_link?: string | null;
  external_link?: string | null;
  channel?: NotificationChannel | string;
  actions?: any;
  metadata?: any;
  message_type?: NotificationMessageType | string;
  follow_up?: string | null;
  quickaction?: NotificationQuickaction | string;
  quickaction_value?: string | null;
}

/**
 * Patch request model according to OpenAPI PatchedNotificationRequest
 */
export interface PatchedNotificationRequest {
  title?: string;
  message?: string | null;
  read_at?: string | null;
  reminder_at?: string | null;
  due_date?: string | null;
  status?: NotificationStatus;
  priority?: NotificationPriority;
  icon?: string | null;
  internal_link?: string | null;
  external_link?: string | null;
  channel?: NotificationChannel | string;
  actions?: any;
  metadata?: any;
  message_type?: NotificationMessageType | string;
  follow_up?: string | null;
  quickaction?: NotificationQuickaction | string;
  quickaction_value?: string | null;
}

/**
 * Frontend Notification Model
 */
export interface Notification {
  id: string;
  title: string;
  message: string;
  createdAt: Date;
  readAt?: Date;
  reminderAt?: Date;
  dueDate?: Date;
  status: NotificationStatus;
  priority: NotificationPriority;
  type: string; // 'SYSTEM' | 'APPOINTMENT' | 'MESSAGE' or 'S' | 'A' | 'M'
  messageType?: NotificationMessageType | string;
  icon?: string;

  // Links
  internalLink?: string;
  externalLink?: string;

  // Kanäle
  channel?: NotificationChannel | string;
  channels: NotificationChannel[];

  // Optionale interaktive Aktionen & Quickactions
  quickaction?: NotificationQuickaction | string;
  quickactionValue?: string;
  followUp?: Date;
  actions?: NotificationAction[];

  // Metadaten für Erweiterbarkeit
  metadata?: Record<string, any>;

  // DTO field aliases
  created_at?: string;
  read_at?: string | null;
  reminder_at?: string | null;
  due_date?: string | null;
  internal_link?: string | null;
  external_link?: string | null;
  message_type?: NotificationMessageType | string;
  quickaction_value?: string | null;
  follow_up?: string | null;
}

export interface NotificationQueryParams {
  channel?: NotificationChannel | string;
  limit?: number;
  offset?: number;
  page?: number;
  page_size?: number;
  ordering?: string;
  message_type?: NotificationMessageType | string;
  priority?: NotificationPriority | string;
  search?: string;
  status?: NotificationStatus | string;
}

export interface PaginatedNotificationResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: NotificationDTO[];
}

export interface MarkAllAsReadResponse {
  status: string;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface ShortLinkCreateRequestRequest {
  target_url: string;
}

export interface ShortLinkCreateResponse {
  target_url: string;
  short_url: string;
}

export interface ShortLinkUpdateRequestRequest {
  short_link: string;
  target_url: string;
}

export interface NotificationResponse {
  id?: string;
  title: string;
  message?: string | null;
  text: string;
  text_html: string;
  text_markdown?: string | null;
  created: string;
  receiver: number;
  sender?: number | null;
  status?: NotificationStatus;
  priority?: NotificationPriority;
  message_type?: NotificationMessageType | string;
  channel?: NotificationChannel | string;
  quickaction?: NotificationQuickaction | string;
  quickaction_value?: string | null;
  follow_up?: string | null;
  icon?: string | null;
  internal_link?: string | null;
  external_link?: string | null;
  read_at?: string | null;
  reminder_at?: string | null;
  due_date?: string | null;
  sent_via_email?: string | null;
  sent_emails?: any[];
  actions?: any;
  metadata?: any;
  template?: string;
  allow_reply?: boolean;
  html_mode?: boolean;
}

export function mapDtoToNotification(dto: NotificationDTO): Notification {
  let type = 'SYSTEM';
  const msgType = dto.message_type ? String(dto.message_type) : '';
  if (msgType === 'A' || msgType === 'APPOINTMENT') {
    type = 'APPOINTMENT';
  } else if (msgType === 'M' || msgType === 'MESSAGE') {
    type = 'MESSAGE';
  } else if (msgType === 'S' || msgType === 'SYSTEM') {
    type = 'SYSTEM';
  }

  let actions: NotificationAction[] | undefined = undefined;
  if (Array.isArray(dto.actions)) {
    actions = dto.actions as NotificationAction[];
  }

  const channel = dto.channel as NotificationChannel;
  const channels: NotificationChannel[] = channel
    ? [channel]
    : [NotificationChannel.WEB];

  return {
    id: dto.id,
    title: dto.title,
    message: dto.message || '',
    createdAt: dto.created_at ? new Date(dto.created_at) : new Date(),
    readAt: dto.read_at ? new Date(dto.read_at) : undefined,
    reminderAt: dto.reminder_at ? new Date(dto.reminder_at) : undefined,
    dueDate: dto.due_date ? new Date(dto.due_date) : undefined,
    status: dto.status || NotificationStatus.UNREAD,
    priority: dto.priority || NotificationPriority.LOW,
    type,
    messageType: dto.message_type,
    icon: dto.icon || undefined,
    internalLink: dto.internal_link || undefined,
    externalLink: dto.external_link || undefined,
    channel: dto.channel || undefined,
    channels,
    quickaction: dto.quickaction || undefined,
    quickactionValue: dto.quickaction_value || undefined,
    followUp: dto.follow_up ? new Date(dto.follow_up) : undefined,
    actions,
    metadata: dto.metadata || undefined,
    created_at: dto.created_at,
    read_at: dto.read_at,
    reminder_at: dto.reminder_at,
    due_date: dto.due_date,
    internal_link: dto.internal_link,
    external_link: dto.external_link,
    message_type: dto.message_type,
    quickaction_value: dto.quickaction_value,
    follow_up: dto.follow_up,
  };
}
